import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from "./AdminShared.module.css";
import { useAdminUI } from "./AdminUIContext";
import {
  itemsToArray,
  getUniqueTypes,
  filterAndSortItems,
  groupItemsByType,
  isTruthyFlag,
  formatDateDisplay,
  toTitleCase,
  getResponseStatus,
  getResponseErrorMessage,
  getItemTypeThemeKey,
} from "./adminUtils";
import {
  // deleteDocFromDb,
  patchACatalogItem,
} from "../../../storage/catalogDB";
import {
  // deleteContentDocFromDb,
  patchAContentItem,
} from "../../../storage/contentDB";
import { isNeverPublished, itemHasDraft } from "../../../data/cmsDraft";
import { previewItemKey } from "../../../data/cmsPreview";
import { usePreviewStores } from "../../../data/CmsPreviewContext";
import {
  formatReleaseCountdown,
  formatReleaseFullDate,
  getReleaseDate,
  isUpcomingRelease,
} from "../../../Hooks/catalogPresentation";

const AdminDataTable = ({ section = "catalog" }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    toast,
    // confirm
  } = useAdminUI();
  const user = useSelector((state) => state.auth.user);
  const catalog = useSelector((state) => state.catalogData.catalog);
  const content = useSelector((state) => state.contentData.content);
  const { previewItems } = usePreviewStores();

  const raw = section === "content" ? content : catalog;
  const allItems = useMemo(() => itemsToArray(raw), [raw]);
  const types = useMemo(() => getUniqueTypes(allItems), [allItems]);

  const [search, setSearch] = useState(searchParams.get("q") || "");
  const [type, setType] = useState(searchParams.get("type") || "");
  const [active, setActive] = useState(searchParams.get("active") || "");
  const [releaseFilter, setReleaseFilter] = useState(
    searchParams.get("release") || "",
  );
  const [sortKey, setSortKey] = useState("title");
  const [sortDir, setSortDir] = useState("asc");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const t = searchParams.get("type") || "";
    const q = searchParams.get("q") || "";
    const a = searchParams.get("active") || "";
    const r = searchParams.get("release") || "";
    setType(t);
    setSearch(q);
    setActive(a);
    setReleaseFilter(r);
  }, [searchParams]);

  const previewKeySet = useMemo(() => {
    const set = new Set();
    (previewItems || []).forEach((item) => {
      if ((item?.section || "content") === section) {
        set.add(previewItemKey(item));
      }
    });
    return set;
  }, [previewItems, section]);

  const itemIsInPreview = (item) =>
    Boolean(item?.identifier) &&
    previewKeySet.has(previewItemKey({ section, identifier: item.identifier }));

  const inPreviewCount = useMemo(
    () =>
      allItems.filter(
        (item) =>
          item?.identifier &&
          previewKeySet.has(
            previewItemKey({ section, identifier: item.identifier }),
          ),
      ).length,
    [allItems, previewKeySet, section],
  );

  const filtered = useMemo(() => {
    let list = filterAndSortItems(allItems, {
      search,
      type,
      active,
      sortKey,
      sortDir,
    });
    if (section === "catalog" && releaseFilter === "upcoming") {
      list = list.filter((item) => isUpcomingRelease(item));
    } else if (section === "catalog" && releaseFilter === "released") {
      list = list.filter((item) => {
        const d = getReleaseDate(item);
        return d && !isUpcomingRelease(item);
      });
    } else if (section === "catalog" && releaseFilter === "no-date") {
      list = list.filter((item) => !getReleaseDate(item));
    }
    if (active === "preview") {
      list = list.filter(
        (item) =>
          item?.identifier &&
          previewKeySet.has(
            previewItemKey({ section, identifier: item.identifier }),
          ),
      );
    }
    return list;
  }, [
    allItems,
    search,
    type,
    active,
    sortKey,
    sortDir,
    section,
    releaseFilter,
    previewKeySet,
  ]);

  const groups = useMemo(() => groupItemsByType(filtered), [filtered]);

  const upcomingCount = useMemo(
    () =>
      section === "catalog"
        ? allItems.filter((item) => isUpcomingRelease(item)).length
        : 0,
    [allItems, section],
  );

  const basePath = `/manage/${section}`;
  const sectionLabel = section === "content" ? "Content" : "Catalog";

  const syncParams = (next) => {
    const params = new URLSearchParams();
    if (next.q) params.set("q", next.q);
    if (next.type) params.set("type", next.type);
    if (next.active) params.set("active", next.active);
    if (next.release) params.set("release", next.release);
    setSearchParams(params, { replace: true });
  };

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return "";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const goEdit = (item) => {
    navigate(`${basePath}/edit/${encodeURIComponent(item._id)}`);
  };

  // const handleDelete = async (e, item) => {
  //   e.stopPropagation();
  //   const ok = await confirm({
  //     title: "Delete item?",
  //     message: `Permanently delete "${item.title || item.slug || item._id}" (${
  //       item.type || "item"
  //     })? This cannot be undone.`,
  //     confirmLabel: "Delete",
  //     danger: true,
  //   });
  //   if (!ok) return;

  //   const identifier = item.identifier;
  //   if (!identifier) {
  //     toast.error("This item has no identifier and cannot be deleted via API.");
  //     return;
  //   }

  //   setBusyId(item._id);
  //   try {
  //     const deleteFn =
  //       section === "content" ? deleteContentDocFromDb : deleteDocFromDb;
  //     const res = await deleteFn(identifier, user);
  //     const status = getResponseStatus(res);
  //     if (status >= 200 && status < 300) {
  //       toast.success("Item deleted. Refreshing data…");
  //       window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
  //     } else {
  //       toast.error(getResponseErrorMessage(res));
  //     }
  //   } catch (err) {
  //     toast.error(err.message || "Delete failed");
  //   } finally {
  //     setBusyId(null);
  //   }
  // };

  const handleToggleActive = async (e, item) => {
    e.stopPropagation();
    const identifier = item.identifier;
    if (!identifier) {
      toast.error("Missing identifier — cannot update.");
      return;
    }
    const nextActive = isTruthyFlag(item.active) ? "false" : "true";
    if (nextActive === "true" && isNeverPublished(item)) {
      toast.error("Publish this item before activating it.");
      return;
    }
    setBusyId(item._id);
    try {
      const patchFn =
        section === "content" ? patchAContentItem : patchACatalogItem;
      const res = await patchFn(identifier, { active: nextActive }, user);
      const status = getResponseStatus(res);
      if (status >= 200 && status < 300) {
        toast.success(
          nextActive === "true" ? "Marked active." : "Marked inactive.",
        );
        window.dispatchEvent(new CustomEvent("ms1-admin-refresh-data"));
      } else {
        toast.error(getResponseErrorMessage(res));
      }
    } catch (err) {
      toast.error(err.message || "Update failed");
    } finally {
      setBusyId(null);
    }
  };

  const renderInPreviewBadge = (item) => {
    if (!itemIsInPreview(item)) return null;
    return (
      <span
        className={`${styles.badge} ${styles["badge-in-preview"]}`}
        title="Included in the admin preview set"
      >
        In preview
      </span>
    );
  };

  const renderUpcomingBadge = (item, { withDetail = false } = {}) => {
    if (!isUpcomingRelease(item)) return null;
    const drop = formatReleaseFullDate(item);
    const countdown = formatReleaseCountdown(item);
    const title = [drop && `Drops ${drop}`, countdown]
      .filter(Boolean)
      .join(" · ");
    return (
      <span
        className={`${styles.badge} ${styles["badge-upcoming"]}`}
        title={title || "Upcoming release"}
      >
        Upcoming
        {withDetail && drop ? ` · ${drop}` : ""}
      </span>
    );
  };

  const renderFlags = (item) => {
    const flags = [];
    if (isTruthyFlag(item.isDefaultPlaylist)) flags.push("default playlist");
    if (isTruthyFlag(item.isFeaturedPlaylist)) flags.push("featured");
    if (isTruthyFlag(item.isDefaultService)) flags.push("default service");
    if (isTruthyFlag(item.addToNavMenu)) flags.push("nav");
    const upcoming = renderUpcomingBadge(item);
    if (!flags.length && !upcoming)
      return <span className={styles["row-muted"]}>—</span>;
    return (
      <span className={styles["flag-row"]}>
        {upcoming}
        {flags.map((f) => (
          <span key={f} className={`${styles.badge} ${styles["badge-flag"]}`}>
            {f}
          </span>
        ))}
      </span>
    );
  };

  /** Secondary list column: slug (catalog) or slug / social name (content). */
  const secondaryLabel = (item) => {
    if (section === "content") {
      return item.slug || item.name || item.profileLink || item.link || "—";
    }
    return item.slug || "—";
  };

  const secondaryColumnHeader = section === "content" ? "Slug / key" : "Slug";

  const renderDesktopRows = (items) =>
    items.map((item) => {
      const rowTypeKey = getItemTypeThemeKey(item.type);
      const rowTypeClass = rowTypeKey ? styles[`item-type-${rowTypeKey}`] : "";
      const inPreview = itemIsInPreview(item);
      const rowClass = [rowTypeClass, inPreview ? styles["row-in-preview"] : ""]
        .filter(Boolean)
        .join(" ");
      return (
        <tr
          key={item._id}
          onClick={() => goEdit(item)}
          className={rowClass || undefined}
        >
          <td>
            <div className={styles["row-title-stack"]}>
              <div className={styles["row-title"]}>
                {item.title || item.name || "Untitled"}
              </div>
              {(inPreview ||
                isUpcomingRelease(item) ||
                itemHasDraft(item) ||
                isNeverPublished(item)) && (
                <div className={styles["row-title-badges"]}>
                  {renderInPreviewBadge(item)}
                  {itemHasDraft(item) && (
                    <span
                      className={`${styles.badge} ${styles["badge-draft"]}`}
                      title="Unpublished draft saved"
                    >
                      Draft
                    </span>
                  )}
                  {isNeverPublished(item) && (
                    <span
                      className={`${styles.badge} ${styles["badge-never-published"]}`}
                      title="Not on the live site yet"
                    >
                      Never published
                    </span>
                  )}
                  {renderUpcomingBadge(item, { withDetail: true })}
                </div>
              )}
            </div>
          </td>
          <td className={styles["row-muted"]}>{secondaryLabel(item)}</td>
          <td>
            <span
              className={`${styles.badge} ${
                isTruthyFlag(item.active)
                  ? styles["badge-active"]
                  : styles["badge-inactive"]
              }`}
            >
              {isTruthyFlag(item.active) ? "Active" : "Inactive"}
            </span>
          </td>
          <td>{renderFlags(item)}</td>
          <td className={styles["row-muted"]}>
            {formatDateDisplay(item.updatedAt || item.createdAt)}
          </td>
          <td onClick={(e) => e.stopPropagation()}>
            <div className={styles["row-actions"]}>
              <button
                type="button"
                className={
                  styles["btn-activate-item"] +
                  " " +
                  (isTruthyFlag(item.active)
                    ? styles["btn-deactivate"]
                    : styles["btn-activate"])
                }
                disabled={busyId === item._id}
                onClick={(e) => handleToggleActive(e, item)}
              >
                {isTruthyFlag(item.active) ? "Deactivate" : "Activate"}
              </button>
              <button
                type="button"
                className={styles["btn-ghost"]}
                disabled={busyId === item._id}
                onClick={() => goEdit(item)}
              >
                Edit
              </button>

              {/* <button
                type="button"
                className={styles["btn-danger"]}
                disabled={busyId === item._id}
                onClick={(e) => handleDelete(e, item)}
              >
                Delete
              </button> */}
            </div>
          </td>
        </tr>
      );
    });

  const renderMobileCards = (items) =>
    items.map((item) => {
      const rowTypeKey = getItemTypeThemeKey(item.type);
      const rowTypeClass = rowTypeKey ? styles[`item-type-${rowTypeKey}`] : "";
      const inPreview = itemIsInPreview(item);
      return (
        <div
          key={item._id}
          className={`${styles["mobile-card"]} ${rowTypeClass} ${
            inPreview ? styles["row-in-preview"] : ""
          }`.trim()}
          onClick={() => goEdit(item)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter") goEdit(item);
          }}
        >
          <p className={styles["mobile-card-title"]}>
            {item.title || item.name || "Untitled"}
          </p>
          <div className={styles["mobile-card-meta"]}>
            {rowTypeKey ? (
              <span
                className={`${styles["badge-item-type"]} ${styles[`item-type-${rowTypeKey}`]}`}
              >
                {toTitleCase(item.type)}
              </span>
            ) : null}
            <span
              className={`${styles.badge} ${
                isTruthyFlag(item.active)
                  ? styles["badge-active"]
                  : styles["badge-inactive"]
              }`}
            >
              {isTruthyFlag(item.active) ? "Active" : "Inactive"}
            </span>
            {renderInPreviewBadge(item)}
            {itemHasDraft(item) && (
              <span className={`${styles.badge} ${styles["badge-draft"]}`}>
                Draft
              </span>
            )}
            {isNeverPublished(item) && (
              <span
                className={`${styles.badge} ${styles["badge-never-published"]}`}
              >
                Never published
              </span>
            )}
            {renderUpcomingBadge(item, { withDetail: true })}
          </div>
          <div className={styles["row-actions"]}>
            {" "}
            <button
              type="button"
              className={
                styles["btn-activate-item"] +
                " " +
                (isTruthyFlag(item.active)
                  ? styles["btn-deactivate"]
                  : styles["btn-activate"])
              }
              disabled={busyId === item._id}
              onClick={(e) => handleToggleActive(e, item)}
            >
              {isTruthyFlag(item.active) ? "Deactivate" : "Activate"}
            </button>
            <button
              type="button"
              className={styles["btn-ghost"]}
              onClick={(e) => {
                e.stopPropagation();
                goEdit(item);
              }}
            >
              Edit
            </button>
          </div>
        </div>
      );
    });

  return (
    <div>
      <div className={styles["page-header"]}>
        <div className={styles["page-header-content"]}>
          <h1 className={styles["page-title"]}>{sectionLabel}</h1>
          <p className={styles["page-subtitle"]}>
            {filtered.length} of {allItems.length} items
            {type
              ? ` · type “${type}”`
              : ` · ${groups.length} type group${groups.length === 1 ? "" : "s"}`}
            {section === "catalog" && upcomingCount > 0
              ? ` · ${upcomingCount} upcoming`
              : ""}
            {inPreviewCount > 0 ? ` · ${inPreviewCount} in preview` : ""}
            {releaseFilter === "upcoming" ? " · showing upcoming only" : ""}
          </p>
        </div>
        <div className={styles["page-actions"]}>
          <Link to={`${basePath}/new`} className={styles["btn-primary"]}>
            + New {section === "content" ? "content" : "catalog"} item
          </Link>
        </div>
      </div>

      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles["search-input"]}
          placeholder={
            section === "content"
              ? "Search title, slug, type, name…"
              : "Search title, slug, type…"
          }
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            syncParams({
              q: e.target.value,
              type,
              active,
              release: releaseFilter,
            });
          }}
          aria-label="Search items"
        />
        <select
          className={styles["select-input"]}
          value={type}
          onChange={(e) => {
            setType(e.target.value);
            syncParams({
              q: search,
              type: e.target.value,
              active,
              release: releaseFilter,
            });
          }}
          aria-label="Filter by type"
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t} value={t}>
              {toTitleCase(t)}
            </option>
          ))}
        </select>
        <select
          className={styles["select-input"]}
          value={active}
          onChange={(e) => {
            setActive(e.target.value);
            syncParams({
              q: search,
              type,
              active: e.target.value,
              release: releaseFilter,
            });
          }}
          aria-label="Filter by active status"
        >
          <option value="">Active & inactive</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
          <option value="draft">Drafts</option>
          <option value="preview">
            In preview
            {inPreviewCount > 0 ? ` (${inPreviewCount})` : ""}
          </option>
        </select>
        {section === "catalog" && (
          <select
            className={styles["select-input"]}
            value={releaseFilter}
            onChange={(e) => {
              setReleaseFilter(e.target.value);
              syncParams({
                q: search,
                type,
                active,
                release: e.target.value,
              });
            }}
            aria-label="Filter by release status"
          >
            <option value="">All release dates</option>
            <option value="upcoming">
              Upcoming only
              {upcomingCount > 0 ? ` (${upcomingCount})` : ""}
            </option>
            <option value="released">Released (has past/today date)</option>
            <option value="no-date">No release date</option>
          </select>
        )}
      </div>

      {allItems.length === 0 ? (
        <div className={styles.panel}>
          <div className={styles["empty-state"]}>
            <h3>No {sectionLabel.toLowerCase()} items</h3>
            <p>Create your first item to get started.</p>
            <Link to={`${basePath}/new`} className={styles["btn-primary"]}>
              Create item
            </Link>
          </div>
        </div>
      ) : groups.length === 0 ? (
        <div className={styles.panel}>
          <div className={styles["empty-state"]}>
            <h3>
              {active === "preview" && inPreviewCount === 0
                ? "Nothing in preview"
                : "No matches"}
            </h3>
            <p>
              {active === "preview" && inPreviewCount === 0
                ? "Include an item from its editor and it will show up here."
                : "Try clearing search or filters."}
            </p>
            <button
              type="button"
              className={styles["btn-ghost"]}
              onClick={() => {
                setSearch("");
                setType("");
                setActive("");
                setReleaseFilter("");
                setSearchParams({}, { replace: true });
              }}
            >
              Clear filters
            </button>
          </div>
        </div>
      ) : (
        <div className={styles["type-groups"]}>
          {groups.map((group) => {
            const typeKey = getItemTypeThemeKey(group.type);
            const typeClass = typeKey ? styles[`item-type-${typeKey}`] : "";
            return (
              <section
                key={group.type}
                className={`${styles["type-group"]} ${typeClass}`.trim()}
                aria-labelledby={`type-group-${section}-${group.type}`}
              >
                <header className={styles["type-group-header"]}>
                  <h2
                    id={`type-group-${section}-${group.type}`}
                    className={styles["type-group-title"]}
                  >
                    <span
                      className={
                        typeKey
                          ? `${styles["badge-item-type"]} ${typeClass}`
                          : `${styles.badge} ${styles["badge-type"]}`
                      }
                    >
                      {toTitleCase(group.type)}
                    </span>
                    <span className={styles["type-group-count"]}>
                      {group.items.length} item
                      {group.items.length === 1 ? "" : "s"}
                    </span>
                  </h2>
                </header>

                <div
                  className={`${styles["table-wrap"]} ${styles["table-desktop"]}`}
                >
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th
                          className={styles.sortable}
                          onClick={() => onSort("title")}
                        >
                          Title{sortIndicator("title")}
                        </th>
                        <th
                          className={styles.sortable}
                          onClick={() => onSort("slug")}
                        >
                          {secondaryColumnHeader}
                          {sortIndicator("slug")}
                        </th>
                        <th
                          className={styles.sortable}
                          onClick={() => onSort("active")}
                        >
                          Status{sortIndicator("active")}
                        </th>
                        <th>Flags</th>
                        <th
                          className={styles.sortable}
                          onClick={() => onSort("updatedAt")}
                        >
                          Updated{sortIndicator("updatedAt")}
                        </th>
                        <th style={{ textAlign: "right" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>{renderDesktopRows(group.items)}</tbody>
                  </table>
                </div>

                <div className={styles["mobile-cards"]}>
                  {renderMobileCards(group.items)}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminDataTable;
